import { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';

export function usePaymentSnooze(trackingId: Id<'paymentTracking'>) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const snoozePolicy = useMutation(api.insurance.paymentTracking.snoozePolicy);
  const unsnoozePolicy = useMutation(api.insurance.paymentTracking.unsnoozePolicy);

  function resetForm() {
    setSelectedDate(undefined);
    setNote('');
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) resetForm();
  }

  async function handleUnsnooze() {
    setIsSaving(true);
    try {
      await unsnoozePolicy({ trackingId });
      setOpen(false);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSave() {
    if (!selectedDate || note.trim().length === 0) return;
    setIsSaving(true);
    try {
      await snoozePolicy({
        trackingId,
        snoozedUntil: selectedDate.getTime(),
        snoozeNote: note.trim(),
      });
      setOpen(false);
      resetForm();
    } finally {
      setIsSaving(false);
    }
  }

  const canSave = !!selectedDate && note.trim().length > 0 && !isSaving;

  return {
    open,
    selectedDate,
    note,
    isSaving,
    canSave,
    setSelectedDate,
    setNote,
    handleOpenChange,
    handleUnsnooze,
    handleSave,
  };
}
